// ABOUTME: Workspace detail page with tabs for overview, data sources, and config.
// ABOUTME: Shows workspace info, stats, and provides access to agent configuration.

import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Users,
  Database,
  FileText,
  Search,
  Settings,
  Edit,
  Save,
  X,
  Trash2,
  Plus,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { workspaceApi, dataSourceApi } from '@/services/api/adminClient';
import type {
  WorkspaceStats,
  SubscriptionTier,
  SubscriptionStatus,
  DataSource,
} from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AgentConfigPanel } from './AgentConfigPanel';
import { DataSourceModal } from './DataSourceModal';
import { WorkspaceUsersTab } from './WorkspaceUsersTab';

interface WorkspaceDetailData {
  id: string;
  name: string;
  slug: string;
  subscription_tier: SubscriptionTier;
  subscription_status: SubscriptionStatus;
  created_at: string;
  data_sources: DataSource[];
}

type TabId = 'overview' | 'users' | 'data-sources' | 'config';

const TABS: { id: TabId; label: string; icon: typeof Building2 }[] = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'data-sources', label: 'Data Sources', icon: Database },
  { id: 'config', label: 'Agent Config', icon: Settings },
];

const TIERS: SubscriptionTier[] = ['freemium', 'basic', 'standard', 'enterprise', 'enterprise_plus'];
const STATUSES: SubscriptionStatus[] = ['active', 'trial', 'suspended', 'cancelled'];

export function WorkspaceDetail() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<WorkspaceDetailData | null>(null);
  const [stats, setStats] = useState<WorkspaceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Data source modal state
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [editingDataSource, setEditingDataSource] = useState<DataSource | undefined>(undefined);
  const [deletingDataSourceId, setDeletingDataSourceId] = useState<string | null>(null);
  const [syncingDataSourceId, setSyncingDataSourceId] = useState<string | null>(null);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editTier, setEditTier] = useState<SubscriptionTier>('freemium');
  const [editStatus, setEditStatus] = useState<SubscriptionStatus>('active');

  const activeTab = (searchParams.get('tab') as TabId) || 'overview';

  useEffect(() => {
    async function loadWorkspace() {
      if (!workspaceId) return;

      try {
        setLoading(true);
        const [workspaceData, statsData] = await Promise.all([
          workspaceApi.get(workspaceId),
          workspaceApi.getStats(workspaceId),
        ]);
        setWorkspace(workspaceData as WorkspaceDetailData);
        setStats(statsData);

        // Initialize edit form
        const ws = workspaceData as WorkspaceDetailData;
        setEditName(ws.name);
        setEditTier(ws.subscription_tier);
        setEditStatus(ws.subscription_status);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspace');
      } finally {
        setLoading(false);
      }
    }
    loadWorkspace();
  }, [workspaceId]);

  const handleTabChange = (tabId: TabId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    setSearchParams(params);
  };

  const handleSave = async () => {
    if (!workspaceId || !workspace) return;

    try {
      setSaving(true);
      await workspaceApi.update(workspaceId, {
        name: editName !== workspace.name ? editName : undefined,
        subscription_tier: editTier !== workspace.subscription_tier ? editTier : undefined,
        subscription_status: editStatus !== workspace.subscription_status ? editStatus : undefined,
      });

      setWorkspace({
        ...workspace,
        name: editName,
        subscription_tier: editTier,
        subscription_status: editStatus,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update workspace');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (workspace) {
      setEditName(workspace.name);
      setEditTier(workspace.subscription_tier);
      setEditStatus(workspace.subscription_status);
    }
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!workspaceId) return;

    try {
      setDeleting(true);
      await workspaceApi.delete(workspaceId);
      navigate('/admin/workspaces');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete workspace');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDataSourceSaved = (dataSource: DataSource) => {
    if (!workspace) return;

    const existingIndex = workspace.data_sources.findIndex((ds) => ds.id === dataSource.id);
    if (existingIndex >= 0) {
      // Update existing
      const updatedDataSources = [...workspace.data_sources];
      updatedDataSources[existingIndex] = dataSource;
      setWorkspace({ ...workspace, data_sources: updatedDataSources });
    } else {
      // Add new
      setWorkspace({ ...workspace, data_sources: [...workspace.data_sources, dataSource] });
    }
  };

  const handleDeleteDataSource = async (dataSourceId: string) => {
    if (!workspaceId || !workspace) return;

    try {
      setDeletingDataSourceId(dataSourceId);
      await dataSourceApi.remove(workspaceId, dataSourceId);
      setWorkspace({
        ...workspace,
        data_sources: workspace.data_sources.filter((ds) => ds.id !== dataSourceId),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete data source');
    } finally {
      setDeletingDataSourceId(null);
    }
  };

  const handleSyncSchema = async (dataSourceId: string) => {
    if (!workspaceId) return;

    try {
      setSyncingDataSourceId(dataSourceId);
      setError(null);
      const result = await dataSourceApi.syncSchema(workspaceId, dataSourceId);
      // Show success feedback (could use toast, but for now we'll rely on the UI state change)
      console.log('Schema sync completed:', result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync schema');
    } finally {
      setSyncingDataSourceId(null);
    }
  };

  const openEditDataSource = (dataSource: DataSource) => {
    setEditingDataSource(dataSource);
    setShowDataSourceModal(true);
  };

  const openAddDataSource = () => {
    setEditingDataSource(undefined);
    setShowDataSourceModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/workspaces"
          className="inline-flex items-center gap-2 text-sm text-surface-600 hover:text-surface-800"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Workspaces
        </Link>
        <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
          {error || 'Workspace not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-6">
      {/* Back Link */}
      <Link
        to="/admin/workspaces"
        className="inline-flex items-center gap-2 text-sm text-surface-600 hover:text-surface-800"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Workspaces
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-sm bg-primary-50 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-surface-800">
              {workspace.name}
            </h1>
            <p className="text-sm text-surface-500">{workspace.slug}</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Card variant="surface" className="border-error/30 bg-error-light">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-sm bg-error/20">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-surface-800">Delete Workspace</h3>
              <p className="text-sm text-surface-600 mt-1">
                Are you sure you want to delete &quot;{workspace.name}&quot;? This action cannot be undone.
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  isLoading={deleting}
                >
                  Delete Workspace
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-200 pb-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors cursor-pointer ${
              activeTab === tab.id
                ? 'bg-primary-50 text-primary-600'
                : 'text-surface-600 hover:text-surface-800 hover:bg-surface-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Edit Form */}
          {editing && (
            <Card variant="surface">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-surface-800">Edit Workspace</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Subscription Tier
                    </label>
                    <select
                      value={editTier}
                      onChange={(e) => setEditTier(e.target.value as SubscriptionTier)}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                    >
                      {TIERS.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Status
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value as SubscriptionStatus)}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
                    >
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={handleCancel}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button isLoading={saving} onClick={handleSave}>
                    <Save className="w-4 h-4 mr-1" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Users"
                value={stats.total_users}
                icon={<Users className="w-5 h-5" />}
                color="indigo"
              />
              <StatCard
                title="Data Sources"
                value={stats.total_data_sources}
                icon={<Database className="w-5 h-5" />}
                color="teal"
              />
              <StatCard
                title="Investigations"
                value={stats.total_investigations}
                icon={<Search className="w-5 h-5" />}
                color="warning"
              />
              <StatCard
                title="Reports"
                value={stats.total_reports}
                icon={<FileText className="w-5 h-5" />}
                color="coral"
              />
            </div>
          )}

          {/* Workspace Info */}
          <Card variant="surface">
            <h3 className="text-sm font-medium text-surface-800 mb-4">
              Workspace Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-surface-500">ID</p>
                <p className="text-surface-800 font-mono">{workspace.id}</p>
              </div>
              <div>
                <p className="text-surface-500">Slug</p>
                <p className="text-surface-800">{workspace.slug}</p>
              </div>
              <div>
                <p className="text-surface-500">Subscription Tier</p>
                <TierBadge tier={workspace.subscription_tier} />
              </div>
              <div>
                <p className="text-surface-500">Status</p>
                <StatusBadge status={workspace.subscription_status} />
              </div>
              <div>
                <p className="text-surface-500">Created</p>
                <p className="text-surface-800">
                  {new Date(workspace.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>

        </div>
      )}

      {/* Data Sources Tab */}
      {activeTab === 'data-sources' && workspaceId && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-surface-800">Data Sources</h3>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={openAddDataSource}
              >
                Add Data Source
              </Button>
            </div>
          </div>

          {workspace.data_sources.length === 0 ? (
            <Card variant="glass" className="p-8 text-center">
              <Database className="w-12 h-12 text-surface-500 mx-auto mb-4" />
              <p className="text-surface-600">No data sources configured</p>
              <p className="text-sm text-surface-500 mt-1">
                Add a data source to start analyzing data
              </p>
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={openAddDataSource}
                >
                  Add Data Source
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {workspace.data_sources.map((ds) => {
                return (
                  <Card key={ds.id} variant="surface">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-success-light">
                          <Database className="w-5 h-5 text-success" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-surface-800">
                            {ds.name}
                          </h4>
                          <p className="text-xs text-surface-500">
                            {ds.type} · {ds.host}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <DataSourceStatusBadge status={ds.status} />
                        {/* Only show sync button for data sources that expose a catalog */}
                        {ds.catalog && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSyncSchema(ds.id)}
                            isLoading={syncingDataSourceId === ds.id}
                            title="Sync schema and regenerate prompts"
                          >
                            <RefreshCw className={`w-4 h-4 ${syncingDataSourceId === ds.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDataSource(ds)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDataSource(ds.id)}
                          isLoading={deletingDataSourceId === ds.id}
                          className="text-error hover:text-error"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && workspaceId && <WorkspaceUsersTab workspaceId={workspaceId} />}

      {activeTab === 'config' && workspaceId && <AgentConfigPanel workspaceId={workspaceId} />}

      {/* Data Source Modal */}
      {workspaceId && (
        <DataSourceModal
          isOpen={showDataSourceModal}
          onClose={() => {
            setShowDataSourceModal(false);
            setEditingDataSource(undefined);
          }}
          workspaceId={workspaceId}
          dataSource={editingDataSource}
          onSaved={handleDataSourceSaved}
        />
      )}

      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'indigo' | 'teal' | 'warning' | 'coral';
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  const colorClasses = {
    indigo: 'bg-primary-50 text-primary-600',
    teal: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    coral: 'bg-error-light text-error',
  };

  return (
    <Card variant="surface">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-xs text-surface-500">{title}</p>
          <p className="text-xl font-semibold text-surface-800">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    freemium: 'bg-surface-100 text-surface-500',
    basic: 'bg-primary-50 text-primary-600',
    standard: 'bg-success-light text-success',
    enterprise: 'bg-warning-light text-warning',
    enterprise_plus: 'bg-error-light text-error',
  };

  return (
    <span className={`inline-block mt-1 px-2.5 py-1 text-xs font-medium rounded-sm ${colors[tier] || colors.freemium}`}>
      {tier.replace('_', ' ')}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-success-light text-success',
    trial: 'bg-warning-light text-warning',
    suspended: 'bg-error-light text-error',
    cancelled: 'bg-surface-100 text-surface-500',
  };

  return (
    <span className={`inline-block mt-1 px-2.5 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.active}`}>
      {status}
    </span>
  );
}

function DataSourceStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: 'bg-success-light text-success',
    error: 'bg-error-light text-error',
    disabled: 'bg-surface-100 text-surface-500',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.disabled}`}>
      {status}
    </span>
  );
}

export default WorkspaceDetail;
